import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { errMsg } from '../../lib/atoAPI';
import { SimpleTable } from '../ato/AtoBits';

const ItemForm = ({ fields, item, onSave, onClose }) => {
  const [v, setV] = useState(item || {});
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="dsp-item-form">
        <DialogHeader><DialogTitle>{item?.id ? 'Edit item' : 'Add item'}</DialogTitle></DialogHeader>
        <div className="space-y-3">{fields.filter((f) => !f.readonly).map((f) => (
          <div key={f.k} className="space-y-1"><Label className="text-xs">{f.label}</Label>
            {f.type === 'select' ? (
              <Select value={v[f.k] || ''} onValueChange={(x) => setV({ ...v, [f.k]: x })}>
                <SelectTrigger data-testid={`dsp-field-${f.k}`}><SelectValue placeholder="Choose" /></SelectTrigger>
                <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            ) : f.long ? <Textarea value={v[f.k] || ''} onChange={(e) => setV({ ...v, [f.k]: e.target.value })} data-testid={`dsp-field-${f.k}`} />
              : <Input type={f.type === 'date' ? 'date' : 'text'} value={v[f.k] || ''} onChange={(e) => setV({ ...v, [f.k]: e.target.value })} data-testid={`dsp-field-${f.k}`} />}
          </div>))}
          <Button onClick={() => onSave(v)} data-testid="dsp-item-save">Save</Button></div>
      </DialogContent>
    </Dialog>
  );
};

export const RegisterView = ({ reg, api, canEdit }) => {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.register(reg).then(setD).catch((e) => toast.error(errMsg(e))), [api, reg]);
  useEffect(() => { load(); }, [load]);
  const save = async (v) => {
    try { if (v.id) await api.edit(reg, v.id, v); else await api.add(reg, v); toast.success('Saved'); setEdit(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (r) => { if (!window.confirm('Delete this item?')) return; try { await api.remove(reg, r.id); load(); } catch (e) { toast.error(errMsg(e)); } };
  const advance = async (r) => { const note = window.prompt('Note for the incident timeline'); if (note === null) return; try { await api.advance(r.id, note); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!d) return null;
  const cols = d.fields.slice(0, 7).map((f) => ({ k: f.k, label: f.label, render: (r) => <span className="line-clamp-3">{r[f.k] || '—'}</span> }));
  if (canEdit) cols.push({ k: '_a', label: '', render: (r) => (
    <span className="flex gap-1">
      {reg === 'incidents' && r.state !== 'post_incident_review' && <Button size="sm" variant="outline" onClick={() => advance(r)} data-testid={`dsp-incident-advance-${r.id}`}>Advance</Button>}
      <Button size="icon" variant="ghost" onClick={() => setEdit(r)} data-testid={`dsp-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>
      <Button size="icon" variant="ghost" onClick={() => del(r)} data-testid={`dsp-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button></span>) });
  return (
    <div className="space-y-3" data-testid={`dsp-register-${reg}`}>
      {canEdit && <Button size="sm" onClick={() => setEdit({})} data-testid={`dsp-add-${reg}`}><Plus className="mr-1 h-4 w-4" />Add</Button>}
      <SimpleTable testId={`dsp-table-${reg}`} rows={d.items} cols={cols} empty="No items recorded." />
      {reg === 'incidents' && d.items.map((r) => r.timeline?.length > 0 && (
        <details key={r.id} className="text-xs"><summary className="cursor-pointer">{r.title} — timeline</summary>
          <ol className="ml-4 list-decimal">{r.timeline.map((t, i) => <li key={i}>{t.at.slice(0, 16).replace('T', ' ')} · {t.state} · {t.note}</li>)}</ol></details>))}
      {edit && <ItemForm fields={d.fields} item={edit} onSave={save} onClose={() => setEdit(null)} />}
    </div>
  );
};
